import { useState, useEffect } from 'react'
import { useAtom } from 'jotai'
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Divider,
  Stack,
} from '@mui/material'
import { Save as SaveIcon, Code as CodeIcon, Visibility as PreviewIcon } from '@mui/icons-material'
import LoadingSpinner from '../components/LoadingSpinner'
import PageHelpButton from '../components/PageHelpButton'
import { usersApi, User } from '../services/api'
import { userAtom } from '../state/auth'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

// Generate default email signature
function generateDefaultSignature(user: User): string {
  const roleLabels: Record<string, string> = {
    sales: 'Sales',
    client_success: 'Client Success Manager',
    resume_writer: 'Resume Writer',
    admin: 'Administrator',
    manager: 'Manager',
  }

  const roleLabel = roleLabels[user.role] || 'Team Member'
  const companyPhone = '(312) 380-5682'

  return `<table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, sans-serif; color: #333; font-size: 14px; line-height: 1.6;">
  <tr>
    <td style="padding-bottom: 10px;">
      <strong style="font-size: 16px;">${user.name}</strong><br>
      <span style="color: #666;">${roleLabel}</span>
    </td>
  </tr>
  <tr>
    <td style="padding-bottom: 10px;">
      <a href="mailto:${user.email}" style="color: #333; text-decoration: none;">${user.email}</a><br>
      <a href="tel:+13123805682" style="color: #333; text-decoration: none;">${companyPhone}</a>
    </td>
  </tr>
  <tr>
    <td style="padding-top: 15px;">
      <img src="https://ci3.googleusercontent.com/mail-sig/AIorK4w3UcEN0-RKnlwsCOmrIN1Sj-uBdma_qksqD6RLaXTI7IO6Vqy6KGqRSgYo-1-iAJNxMse0K-q_pkY4" alt="MissFit Coaching" style="display: block; border: 0; max-width: 200px;">
    </td>
  </tr>
</table>`
}

export default function Profile() {
  const [currentUser] = useAtom(userAtom)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [profile, setProfile] = useState<User | null>(null)
  const [name, setName] = useState('')
  const [emailSignature, setEmailSignature] = useState('')
  const [tabValue, setTabValue] = useState(0)
  const [previewMode, setPreviewMode] = useState(false)

  useEffect(() => {
    loadProfile()
  }, [])

  const loadProfile = async () => {
    try {
      setLoading(true)
      setError(null)

      const users = await usersApi.list({ includeDeleted: false })
      const user = users.find((u) => u.email === currentUser?.email)

      if (user) {
        setProfile(user)
        setName(user.name)
        // If no signature exists, generate default but mark it as unsaved
        const defaultSig = generateDefaultSignature(user)
        setEmailSignature(user.emailSignature || defaultSig)
      } else {
        setError('Unable to load profile')
      }
    } catch (err) {
      console.error('Error loading profile:', err)
      setError('Failed to load profile')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)
      setSuccess(null)

      if (!currentUser?.email) {
        setError('No user email found')
        return
      }

      await usersApi.update({
        email: currentUser.email,
        name,
        emailSignature,
      })

      setSuccess('Profile updated successfully')

      // Reload profile to get updated data
      await loadProfile()
    } catch (err) {
      console.error('Error saving profile:', err)
      setError('Failed to save profile')
    } finally {
      setSaving(false)
    }
  }

  const hasChanges = () => {
    if (!profile) return false
    // Check if name changed
    if (name !== profile.name) return true
    // Check if signature changed
    // If profile has a signature, compare against it
    if (profile.emailSignature) {
      return emailSignature !== profile.emailSignature
    }
    // If profile has NO signature, we have changes if user has edited from the default
    // Always return true if there's no saved signature (user needs to save the default)
    return true
  }

  if (loading) {
    return <LoadingSpinner />
  }

  if (!profile) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Unable to load profile</Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>My Profile</Typography>
          <PageHelpButton articleId="page-profile" />
        </Stack>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={!hasChanges() || saving}
        >
          Save Changes
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
            <Tab label="Basic Information" />
            <Tab label="Email Signature" />
          </Tabs>
        </Box>

        <CardContent>
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ maxWidth: 600 }}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <TextField
                label="Email"
                value={profile.email}
                fullWidth
                disabled
                margin="normal"
                helperText="Email cannot be changed"
              />

              <TextField
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                margin="normal"
                helperText="This name will appear throughout the app and in your email signature"
              />

              <TextField
                label="Role"
                value={profile.role}
                fullWidth
                disabled
                margin="normal"
                helperText="Role is managed by administrators"
              />

              <TextField
                label="Phone"
                value={profile.phone || ''}
                fullWidth
                disabled
                margin="normal"
                helperText="Phone is managed by administrators"
              />

              <TextField
                label="Status"
                value={profile.status}
                fullWidth
                disabled
                margin="normal"
              />
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Email Signature</Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={previewMode ? <CodeIcon /> : <PreviewIcon />}
                  onClick={() => setPreviewMode(!previewMode)}
                >
                  {previewMode ? 'Edit HTML' : 'Preview'}
                </Button>
              </Box>
              <Divider sx={{ mb: 3 }} />

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This signature will be automatically added to all emails you send through the app.
              </Typography>

              {previewMode ? (
                <Card variant="outlined" sx={{ p: 3, backgroundColor: 'grey.50' }}>
                  <Typography variant="subtitle2" gutterBottom color="text.secondary">
                    Preview:
                  </Typography>
                  <Box
                    sx={{ mt: 2, p: 2, backgroundColor: 'white', borderRadius: 1 }}
                    dangerouslySetInnerHTML={{ __html: emailSignature }}
                  />
                </Card>
              ) : (
                <TextField
                  label="HTML Signature"
                  value={emailSignature}
                  onChange={(e) => setEmailSignature(e.target.value)}
                  fullWidth
                  multiline
                  rows={16}
                  margin="normal"
                  helperText="Enter HTML code for your email signature. Use inline styles for best email client compatibility."
                  sx={{ fontFamily: 'monospace', '& textarea': { fontFamily: 'monospace', fontSize: '13px' } }}
                />
              )}
            </Box>
          </TabPanel>
        </CardContent>
      </Card>
    </Box>
  )
}
